import type { ButtonProps } from "@mui/material";
import { Button, Menu, MenuItem, Skeleton } from "@mui/material";
import { MessageTemplateCategoriesEnum } from "@sinch/core/entities/serverEnums.types";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsDesignServices } from "@sinch/icons";
import { MessageTemplateParamsInput } from "@sinch/types/sinch.types";
import { isNil, propEq } from "ramda";
import { isNilOrEmpty } from "ramda-adjunct";
import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MessageTemplates } from "./queries.graphql";
import type {
  MessageTemplatesQuery,
  MessageTemplatesQueryVariables,
} from "@sinch/components/application/Messages/queries.types";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { ConditionalWrapper } from "@sinch/components/ConditionalWrapper/ConditionalWrapper";
import { MdsIcon } from "@sinch/components/index";

export interface MessageTemplateSelectInputProps extends ButtonProps {
  availableChannels: NotificationChannel[];
  onApplyTemplate: (templateData: MessageTemplatesQuery["messageTemplates"][0]) => void;
  category?: MessageTemplateCategoriesEnum;
  input?: MessageTemplateParamsInput;
  fillDefaultTemplate?: boolean;
}

export const MessageTemplateSelectMenu: FC<MessageTemplateSelectInputProps> = ({
  availableChannels,
  onApplyTemplate,
  category,
  input,
  fillDefaultTemplate,
  ...props
}) => {
  const { t } = useTranslation();

  const { previousData, data, loading } = useQueryHandler<MessageTemplatesQuery, MessageTemplatesQueryVariables>(
    MessageTemplates,
    {
      filter: { channel: { IN: availableChannels }, ...(category ? { category: { EQ: category } } : {}) },
      input: input,
    }
  );

  useEffect(() => {
    if (fillDefaultTemplate && isNil(previousData) && data && data.messageTemplates.some((template) => template.default)) {
      onApplyTemplate(data.messageTemplates.find(propEq("default", true))!);
    }
  }, [data, previousData]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (val: string) => {
    const templateData = data?.messageTemplates.find(propEq("id", val));
    if (templateData) {
      onApplyTemplate(templateData);
    }
    setAnchorEl(null);
  };

  if (isNilOrEmpty(data?.messageTemplates)) {
    return null;
  }

  return (
    <>
      <ConditionalWrapper condition={loading} wrapper={(children) => <Skeleton variant="rounded">{children}</Skeleton>}>
        <Button
          color="info"
          fullWidth={false}
          onClick={handleClick}
          startIcon={<MdsIcon icon={MdsDesignServices} />}
          sx={{ width: "fit-content", height: "fit-content" }}
          variant="outlined"
          {...props}
        >
          {t("MessageTemplate.template")}
        </Button>
      </ConditionalWrapper>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        disablePortal
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            style: {
              maxHeight: 48 * 4.5,
              width: "fit-content",
            },
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {data?.messageTemplates?.map((option) => (
          <MenuItem key={option.id} onClick={() => handleClose(option.id)}>
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
